import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/sections/Navigation';
import Footer from '@/sections/Footer';

const TermsOfService = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </button>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Terms of Service
          </h1>
          <p className="text-lg text-slate-600">
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-slate max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") and KMG ("Company," "we," "us," or "our") governing your access to and use of our software platform, services, website, and related applications (collectively, the "Service").
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              By accessing, downloading, installing, or using our Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy, which is incorporated herein by reference. If you do not agree to these Terms, you must not access or use the Service.
            </p>
            <p className="text-slate-700 leading-relaxed">
              These Terms apply to all users of the Service, including without limitation users who are browsers, vendors, customers, merchants, and contributors of content. If you are entering into these Terms on behalf of a company or other legal entity, you represent that you have the authority to bind such entity to these Terms.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Description of Service</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              KMG provides a comprehensive data infrastructure platform known as the "KMG Vectoral System" that enables businesses to integrate, manage, and leverage their data assets. Our Service includes:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Data integration and connectivity solutions</li>
              <li>Real-time data processing and analytics capabilities</li>
              <li>API and platform access</li>
              <li>Technical support and maintenance services</li>
              <li>Documentation, training materials, and related resources</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice. We do not guarantee that the Service will be available at all times or that it will be error-free.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Account Registration and Security</h2>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">3.1 Account Creation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To access certain features of the Service, you must register for an account. When creating an account, you agree to:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">3.2 Account Security</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Use a strong, unique password</li>
              <li>Not share your account credentials with any third party</li>
              <li>Log out of your account when using shared or public devices</li>
              <li>Immediately notify us of any suspected security breach</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              We are not liable for any loss or damage arising from your failure to comply with these security obligations.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Acceptable Use</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Violate any applicable local, state, national, or international law or regulation</li>
              <li>Infringe upon the intellectual property rights of others</li>
              <li>Transmit any malicious code, viruses, or harmful software</li>
              <li>Attempt to gain unauthorized access to the Service or related systems</li>
              <li>Interfere with or disrupt the integrity or performance of the Service</li>
              <li>Use the Service to process or store illegal, harmful, or offensive content</li>
              <li>Reverse engineer, decompile, or disassemble any portion of the Service</li>
              <li>Use automated systems (bots, scrapers, etc.) to access the Service without authorization</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Collect or harvest information about other users without their consent</li>
              <li>Use the Service in any manner that could damage, disable, or impair our servers or networks</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              We reserve the right to investigate violations of these Terms and may involve law enforcement authorities in prosecuting users who violate applicable laws.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Intellectual Property Rights</h2>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">5.1 Our Intellectual Property</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Service, including its original content, features, functionality, design, logos, trademarks, and software, is owned by KMG and protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. All rights not expressly granted to you are reserved by KMG.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">5.2 License Grant</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for your internal business purposes. This license does not include:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Any resale or commercial use of the Service</li>
              <li>Collection and use of any product listings or descriptions</li>
              <li>Derivative use of the Service or its contents</li>
              <li>Use of data mining, robots, or similar data gathering tools</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">5.3 Your Content</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              You retain ownership of any data, content, or materials you submit, upload, or transmit through the Service ("Your Content"). By using the Service, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and distribute Your Content solely for the purpose of providing and improving the Service.
            </p>
            <p className="text-slate-700 leading-relaxed">
              You represent and warrant that you own or have the necessary rights to grant the license described above and that Your Content does not violate any third-party rights or applicable laws.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Payment Terms</h2>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">6.1 Fees</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Access to certain features of the Service may require payment of fees. All fees are stated in the currency specified at the time of purchase and are exclusive of applicable taxes. You agree to pay all fees associated with your use of the Service in accordance with the pricing terms presented to you.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">6.2 Billing</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Fees may be billed on a monthly, annual, or other periodic basis as specified in your subscription plan. By providing payment information, you authorize us to charge your payment method for all fees due. If payment is not received by the due date, we may suspend or terminate your access to the Service.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">6.3 Refunds</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Refund policies vary by subscription plan and are specified in your service agreement. Generally, fees paid are non-refundable except as required by law or as expressly stated in your agreement. If you cancel your subscription, you will continue to have access to the Service until the end of your current billing period.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">6.4 Price Changes</h3>
            <p className="text-slate-700 leading-relaxed">
              We reserve the right to modify our pricing at any time. We will provide you with at least 30 days' notice of any price changes. If you do not agree to the new pricing, you may cancel your subscription before the changes take effect.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Data Processing and Privacy</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Your use of the Service is also governed by our Privacy Policy, which explains how we collect, use, and protect your information. By using the Service, you consent to the collection and use of your information as described in our Privacy Policy.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              When you use our Service to process data on behalf of your organization or customers, you are responsible for ensuring that:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>You have the legal right to process such data</li>
              <li>You comply with all applicable data protection laws and regulations</li>
              <li>You have obtained necessary consents from data subjects</li>
              <li>You have appropriate data processing agreements in place</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              We act as a data processor with respect to data you process through our Service and will process such data only in accordance with your instructions and applicable law.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Service Availability and Modifications</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We strive to provide reliable and continuous access to the Service, but we do not guarantee that the Service will be available at all times or free from errors, interruptions, or defects. The Service may be unavailable due to:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Scheduled maintenance and updates</li>
              <li>Unscheduled maintenance or repairs</li>
              <li>Technical failures or system overload</li>
              <li>Force majeure events</li>
              <li>Actions by third parties</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mb-4">
              We reserve the right to modify, update, or discontinue any aspect of the Service at any time, with or without notice. We are not liable to you or any third party for any modification, suspension, or discontinuation of the Service.
            </p>
            <p className="text-slate-700 leading-relaxed">
              We may release updates, patches, or new versions of the Service from time to time. You may be required to install updates to continue using the Service.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Third-Party Services and Links</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Service may integrate with or link to third-party services, websites, or applications. We do not control, endorse, or assume responsibility for any third-party content, services, or practices. Your interactions with third parties are solely between you and the third party.
            </p>
            <p className="text-slate-700 leading-relaxed">
              We are not responsible for the availability, accuracy, or content of third-party services. Your use of third-party services is subject to the terms and conditions of those third parties.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Disclaimers</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT</li>
              <li>WARRANTIES THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, ERROR-FREE, OR FREE FROM VIRUSES</li>
              <li>WARRANTIES REGARDING THE ACCURACY, RELIABILITY, OR COMPLETENESS OF INFORMATION ON THE SERVICE</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              Some jurisdictions do not allow the exclusion of implied warranties, so the above exclusions may not apply to you.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Limitation of Liability</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL KMG, ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
              <li>LOSS OF PROFITS, REVENUE, DATA, OR USE</li>
              <li>BUSINESS INTERRUPTION OR LOSS OF BUSINESS OPPORTUNITIES</li>
              <li>COSTS OF PROCUREMENT OF SUBSTITUTE SERVICES</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mb-4">
              OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE USE OF THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRIOR TO THE EVENT GIVING RISE TO THE LIABILITY, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Some jurisdictions do not allow the exclusion or limitation of certain damages, so the above limitations may not apply to you.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Indemnification</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              You agree to indemnify, defend, and hold harmless KMG, its affiliates, officers, directors, employees, agents, and licensors from and against any and all claims, damages, obligations, losses, liabilities, costs, or debt, and expenses (including reasonable attorneys' fees) arising out of or relating to:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Your use of or access to the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party right, including intellectual property or privacy rights</li>
              <li>Your Content or any data you process through the Service</li>
              <li>Any negligent or wrongful conduct by you or your employees or agents</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              We reserve the right, at our own expense, to assume the exclusive defense and control of any matter subject to indemnification by you, in which event you will cooperate with us in asserting any available defenses.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Termination</h2>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">13.1 Termination by You</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              You may terminate your account and stop using the Service at any time by contacting us or using the account deletion features provided in the Service. Upon termination, your right to access and use the Service will immediately cease.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">13.2 Termination by Us</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We may suspend or terminate your access to the Service immediately, without prior notice or liability, for any reason, including if you breach these Terms. We may also terminate or suspend your account if:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>You fail to pay fees when due</li>
              <li>You engage in fraudulent or illegal activity</li>
              <li>We are required to do so by law</li>
              <li>We discontinue the Service</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">13.3 Effect of Termination</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Upon termination of your account:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Your right to access and use the Service will immediately cease</li>
              <li>We may delete your account and data in accordance with our data retention policies</li>
              <li>All provisions of these Terms that by their nature should survive termination will survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Dispute Resolution</h2>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">14.1 Governing Law</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to its conflict of law provisions.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">14.2 Dispute Resolution Process</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              In the event of any dispute, controversy, or claim arising out of or relating to these Terms or the Service, the parties agree to first attempt to resolve the dispute through good faith negotiations. If the dispute cannot be resolved through negotiations within thirty (30) days, the parties agree to submit the dispute to binding arbitration in accordance with the rules of [Arbitration Organization].
            </p>
            <p className="text-slate-700 leading-relaxed">
              Notwithstanding the foregoing, either party may seek injunctive relief in any court of competent jurisdiction to protect its intellectual property rights or to prevent irreparable harm.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">15. General Provisions</h2>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">15.1 Entire Agreement</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              These Terms, together with our Privacy Policy and any other legal notices published by us on the Service, constitute the entire agreement between you and KMG regarding the Service and supersede all prior agreements and understandings.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">15.2 Severability</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">15.3 Waiver</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              No waiver of any term of these Terms shall be deemed a further or continuing waiver of such term or any other term. Our failure to assert any right or provision under these Terms shall not constitute a waiver of such right or provision.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">15.4 Assignment</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              You may not assign or transfer these Terms or your rights hereunder without our prior written consent. We may assign or transfer these Terms or our rights and obligations hereunder without restriction.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">15.5 Force Majeure</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We shall not be liable for any failure or delay in performance under these Terms due to circumstances beyond our reasonable control, including natural disasters, war, terrorism, labor disputes, pandemics, or failures of third-party services.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">15.6 Changes to Terms</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We reserve the right to modify these Terms at any time. We will notify you of material changes by:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Posting the updated Terms on this page</li>
              <li>Updating the "Last Updated" date</li>
              <li>Sending you an email notification (for significant changes)</li>
              <li>Displaying a prominent notice on the Service</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              Your continued use of the Service after any changes to these Terms constitutes your acceptance of the updated Terms. If you do not agree to the updated Terms, you must stop using the Service.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">16. Contact Information</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-slate-50 rounded-lg p-6 mt-4">
              <p className="text-slate-900 font-semibold mb-2">KMG</p>
              <p className="text-slate-700 mb-1">
                <strong>Email:</strong> info@kmgtechnologies.com
              </p>
              <p className="text-slate-700 mb-1">
                <strong>Address:</strong> [Your Company Address]
              </p>
              {/* <p className="text-slate-700">
                <strong>Phone:</strong> +1 (514) 710-2555
              </p> */}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsOfService;
